import { useCallback, useEffect, useRef, useState } from "react";

interface GoogleTokenResponse {
  access_token: string;
  [key: string]: unknown;
}

interface GoogleTokenClient {
  requestAccessToken: () => void;
}

interface GoogleAccountsOAuth2 {
  initTokenClient: (config: {
    client_id: string;
    scope: string;
    ux_mode: string;
    callback: (response: GoogleTokenResponse) => void;
  }) => GoogleTokenClient;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: GoogleAccountsOAuth2;
      };
    };
  }
}

const GOOGLE_SCOPE =
  "openid email profile https://www.googleapis.com/auth/userinfo.email";

/**
 * Wraps Google Identity Services' token client so Login/Signup only need to
 * call `triggerGoogleSignIn()` and handle the resulting access token via
 * `onToken`. The GSI script itself is loaded in index.html
 * (https://accounts.google.com/gsi/client); this hook waits for it and sets
 * up the client, same as anp-fe's LoginForm/RegisterForm do.
 *
 * Requires VITE_GOOGLE_CLIENT_ID to be set (non-blank) - if it isn't, `isReady` stays
 * false so callers can hide/disable the Google button instead of throwing.
 */
export const useGoogleSignIn = (onToken: (accessToken: string) => void) => {
  // Trimmed so a blank value (e.g. " " in the Amplify console) counts as unset.
  const clientId =
    (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined)?.trim();
  const [isReady, setIsReady] = useState(false);
  const tokenClientRef = useRef<GoogleTokenClient | null>(null);

  // Keep the latest callback without re-running the setup effect below.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!clientId) {
      console.warn(
        "VITE_GOOGLE_CLIENT_ID is not set - Google sign-in is disabled."
      );
      return;
    }

    let cancelled = false;
    let pollId: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const setup = () => {
      if (cancelled || !window.google?.accounts?.oauth2) return;
      tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: GOOGLE_SCOPE,
        ux_mode: "popup",
        callback: (response) => onTokenRef.current(response.access_token),
      });
      setIsReady(true);
    };

    if (window.google?.accounts) {
      setup();
    } else {
      // The GSI script tag is async; poll briefly until it's loaded.
      pollId = setInterval(() => {
        if (window.google?.accounts) {
          clearInterval(pollId);
          setup();
        }
      }, 100);
      timeoutId = setTimeout(() => clearInterval(pollId), 10000);
    }

    return () => {
      cancelled = true;
      clearInterval(pollId);
      clearTimeout(timeoutId);
    };
  }, [clientId]);

  const triggerGoogleSignIn = useCallback(() => {
    tokenClientRef.current?.requestAccessToken();
  }, []);

  return { triggerGoogleSignIn, isReady };
};
